import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router';
import type { Task } from '@cleartask/shared';
import { NAV_ITEMS, useNavItems } from '../../navigation.js';
import { useTaskList } from '../tasks/useTasks.js';
import { useLists } from '../lists/useLists.js';

export interface CommandPaletteProps {
  open: boolean;
  /** Texto inicial. O atalho `/` abre ja em modo busca. */
  initialQuery?: string;
  onClose: () => void;
}

/**
 * Filtro opcional da paleta.
 *
 * A decisao registrada na secao 8d do HANDOFF continua valendo: o PADRAO e
 * unico, porque quem aperta ⌘K quer chegar a algum lugar e ainda nao sabe se
 * esse lugar e uma aba, uma lista ou uma tarefa. Os chips nao invertem isso —
 * eles existem para o segundo momento, quando a busca devolveu trinta linhas e
 * a pessoa JA sabe o que procura. Escolher continua sendo opcional.
 */
type Filtro = 'tudo' | 'tarefas' | 'listas';

const FILTROS: Array<{ id: Filtro; label: string }> = [
  { id: 'tudo', label: 'Tudo' },
  { id: 'tarefas', label: 'Tarefas' },
  { id: 'listas', label: 'Listas' },
];

interface Opcao {
  id: string;
  label: string;
  hint: string;
  run: () => void;
}

/**
 * Paleta de comandos e busca (spec 10.2).
 *
 * Uma superficie so para as duas coisas, e nao duas telas: quem aperta `⌘K`
 * quer chegar a algum lugar, e "algum lugar" tanto pode ser uma aba quanto uma
 * tarefa especifica. Separa-las obrigaria a escolher a ferramenta antes de
 * saber o que se procura.
 *
 * Sem termo digitado, mostra a navegacao. Com termo, a busca no servidor manda —
 * e ela inclui as concluidas, porque procurar por algo que ja foi feito e
 * metade do uso de uma busca.
 */
export function CommandPalette({ open, initialQuery = '', onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState(initialQuery);
  const [indiceAtivo, setIndiceAtivo] = useState(0);
  const [filtro, setFiltro] = useState<Filtro>('tudo');
  const navigate = useNavigate();
  const abas = useNavItems();
  const inputRef = useRef<HTMLInputElement>(null);

  // `initialQuery` so vale na abertura: reagir a ela depois sobrescreveria o
  // que a pessoa esta digitando.
  useEffect(() => {
    if (!open) return;
    setQuery(initialQuery);
    setIndiceAtivo(0);
    inputRef.current?.focus();
  }, [open, initialQuery]);

  const termo = query.trim();
  const { data: listasData } = useLists();
  const listas = listasData?.lists ?? [];

  const { data, isFetching } = useTaskList({
    view: 'list',
    includeDone: true,
    ...(termo.length > 0 ? { q: termo } : {}),
  });

  // A consulta so vale como busca quando ha termo; sem ele o hook devolve a
  // lista inteira, que nao e o que a paleta quer mostrar.
  const resultados: Task[] = termo.length > 0 ? (data?.tasks ?? []) : [];

  const opcoes = useMemo<Opcao[]>(() => {
    // TODAS as telas, e nao so as da barra: esconder uma aba nao pode tornar a
    // tela inalcancavel. So as visiveis mostram tecla, porque so elas tem uma.
    const navegacao: Opcao[] = NAV_ITEMS.map((item) => {
      const posicao = abas.findIndex((aba) => aba.id === item.id);
      return {
        id: `nav-${item.id}`,
        label: item.label,
        hint: posicao >= 0 ? `Ir para ${item.label} · ${posicao + 1}` : `Ir para ${item.label}`,
        run: () => {
          navigate(item.path);
          onClose();
        },
      };
    });

    const casaComTermo = (texto: string) =>
      texto.toLocaleLowerCase('pt-BR').includes(termo.toLocaleLowerCase('pt-BR'));

    const listasEncontradas: Opcao[] = listas
      .filter((lista) => termo.length === 0 || casaComTermo(lista.name))
      .slice(0, 10)
      .map((lista) => ({
        id: `list-${lista.id}`,
        label: lista.name,
        hint: 'lista',
        run: () => {
          navigate(`/tarefas?lista=${lista.id}`);
          onClose();
        },
      }));

    const tarefas: Opcao[] = resultados.slice(0, 20).map((task) => ({
      id: `task-${task.id}`,
      label: task.title,
      hint: task.status === 'done' ? 'concluída' : 'tarefa',
      run: () => {
        // Leva ao ITEM, e nao mais a tela onde ele vive: escolher uma tarefa na
        // paleta e largar quem escolheu numa lista de trinta linhas era a divida
        // que a rota `/tarefas/:id` veio pagar.
        navigate(`/tarefas/${task.id}`);
        onClose();
      },
    }));

    if (filtro === 'tarefas') return tarefas;
    if (filtro === 'listas') return listasEncontradas;

    // Sem termo, a paleta e um menu de navegacao: mostrar a base inteira de
    // tarefas seria ruido, mas as listas cabem porque sao poucas e sao destinos.
    if (termo.length === 0) return [...navegacao, ...listasEncontradas];

    // A navegacao continua alcancavel por termo: digitar "cal" e Enter tem de
    // levar ao Calendario sem obrigar a limpar o campo.
    return [...navegacao.filter((o) => casaComTermo(o.label)), ...listasEncontradas, ...tarefas];
  }, [termo, filtro, resultados, listas, abas, navigate, onClose]);

  // O indice pode ficar fora da lista quando o termo encurta o resultado.
  const ativo = Math.min(indiceAtivo, Math.max(0, opcoes.length - 1));

  function aoTeclar(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIndiceAtivo((i) => Math.min(i + 1, opcoes.length - 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setIndiceAtivo((i) => Math.max(i - 1, 0));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      opcoes[ativo]?.run();
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center bg-brand-navy/40 p-4 pt-[10vh]"
      // Clique fora fecha. O `role` fica no painel, nao aqui: este elemento e so
      // o fundo.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Paleta de comandos"
        className="flex w-full max-w-lg flex-col overflow-hidden rounded bg-surface shadow-card"
      >
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIndiceAtivo(0);
          }}
          onKeyDown={aoTeclar}
          placeholder="Buscar tarefa ou ir para…"
          aria-label="Buscar tarefa ou comando"
          className="min-h-touch border-b border-border bg-surface px-4 text-base outline-none"
        />

        <div
          className="flex gap-1 border-b border-border px-2 py-1.5"
          role="tablist"
          aria-label="Filtrar resultados"
        >
          {FILTROS.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={item.id === filtro}
              onClick={() => {
                setFiltro(item.id);
                setIndiceAtivo(0);
                // O foco volta ao campo: clicar num chip nao pode custar a
                // navegacao por setas que a pessoa estava usando.
                inputRef.current?.focus();
              }}
              className={[
                'rounded-sm px-2 py-1 text-xs transition-colors duration-150',
                item.id === filtro
                  ? 'bg-brand-blue-soft font-medium text-brand-blue'
                  : 'text-text-mut hover:bg-surface-2',
              ].join(' ')}
            >
              {item.label}
            </button>
          ))}
        </div>

        <ul className="max-h-80 overflow-y-auto" role="listbox" aria-label="Resultados">
          {opcoes.length === 0 ? (
            <li className="px-4 py-3 text-sm text-text-mut">
              {isFetching ? 'Buscando…' : `Nada encontrado para “${termo}”.`}
            </li>
          ) : (
            opcoes.map((opcao, i) => (
              <li key={opcao.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === ativo}
                  // `onMouseDown` e nao `onClick`: o clique tira o foco do campo
                  // antes de o `click` disparar, e um `blur` fecharia a paleta
                  // antes de a opcao rodar.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    opcao.run();
                  }}
                  onMouseEnter={() => setIndiceAtivo(i)}
                  className={[
                    'flex min-h-touch w-full items-center justify-between gap-4 px-4 text-left text-sm',
                    i === ativo ? 'bg-brand-blue-soft text-brand-blue' : 'hover:bg-surface-2',
                  ].join(' ')}
                >
                  <span className="min-w-0 flex-1 truncate">{opcao.label}</span>
                  <span className="shrink-0 text-xs text-text-mut">{opcao.hint}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}
